import { useEffect, useState, useCallback, useRef } from "react";
import { api, friendlyError, logIssue } from "@/lib/api";

/**
 * Loads the public site payload. Retries are handled centrally in the axios
 * layer; here we only expose a clear loading / error / ready state so a cold
 * start never turns into an endless spinner or an uncaught exception.
 */
export function useSite() {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: s.data ? "ready" : "loading", error: "" }));
    try {
      const res = await api.get("/site");
      if (mounted.current) setState({ status: "ready", data: res.data, error: "" });
    } catch (e) {
      logIssue("GET /site", e);
      if (mounted.current) setState({ status: "error", data: null, error: friendlyError(e) });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { site: state.data, status: state.status, error: state.error, reload: load };
}
